import { z } from 'zod';
import { LoopEventTypeEnum } from './events.js';

/**
 * Схемы дашборда K-фактора (Веха 8, ARCHITECTURE_PLAN §12, CANON E1/E2/E3).
 *
 * Источник правды:
 *   - threat-model security/threat-models/analytics.md (I.c / контроль #11 —
 *     ТОЛЬКО агрегаты-counts, НИКОГДА не userId/boxId/fingerprint/тексты).
 *   - CANON B1/B2/B4: ответ admin-эндпоинта не должен раскрывать личность даже
 *     косвенно (k-anonymity floor для узких bucket'ов — defer #15, follow-up).
 *
 * Эндпоинт `GET /analytics/k` (admin-only, allowlist `ADMIN_TELEGRAM_IDS`)
 * появится в M3; схема ответа фиксируется в M1, чтобы computeKFactor строился
 * против контракта.
 */

/**
 * Query `GET /analytics/k`. Strict — единственный параметр `?days` (окно
 * агрегации). `coerce` — query приходит строкой; int 1..365, default 30.
 * Граница max=365 (контроль #11/D.c — bounded период, не full-table scan).
 */
export const KFactorQuery = z
  .object({
    days: z.coerce.number().int().min(1).max(365).default(30),
  })
  .strict();
export type KFactorQuery = z.infer<typeof KFactorQuery>;

/**
 * Воронка — counts по КАЖДОМУ из 9 типов `LoopEvent` за период (work-035:
 * +`ad_start`). Построена из `LoopEventTypeEnum` (single-source) — добавление
 * типа в enum заставит обновить и воронку. Только числа (контроль #11).
 */
export const FunnelCounts = z
  .object(
    Object.fromEntries(
      LoopEventTypeEnum.options.map((t) => [t, z.number().int().nonnegative()]),
    ) as Record<z.infer<typeof LoopEventTypeEnum>, z.ZodNumber>,
  )
  .strict();
export type FunnelCounts = z.infer<typeof FunnelCounts>;

/** Разбивка рефералов по каналу за период — только counts. */
export const ChannelBreakdown = z
  .object({
    story: z.number().int().nonnegative(),
    chat: z.number().int().nonnegative(),
    direct: z.number().int().nonnegative(),
  })
  .strict();
export type ChannelBreakdown = z.infer<typeof ChannelBreakdown>;

/**
 * Response `GET /analytics/k`. Strict — ТОЛЬКО агрегаты (контроль #11). Никаких
 * списков userId/boxId, текстов, fingerprint. zod гарантирует shape: future-self
 * не сможет «по случаю» добавить identity-поле без правки контракта.
 */
export const KFactorResponse = z
  .object({
    /** Окно агрегации в днях (echo query `?days`). */
    periodDays: z.number().int().positive(),
    /** Новые атрибутированные авторы за период (count Referral). */
    newAttributedAuthors: z.number().int().nonnegative(),
    /** Активные авторы за период (знаменатель K). */
    activeAuthors: z.number().int().nonnegative(),
    /** K = newAttributedAuthors / activeAuthors (0 при делении на 0). */
    kFactor: z.number().nonnegative(),
    /** Counts по типам событий воронки. */
    funnel: FunnelCounts,
    /** Counts рефералов по каналу. */
    channelBreakdown: ChannelBreakdown,
    /** ISO-таймштамп генерации ответа. */
    generatedAt: z.string(),
  })
  .strict();
export type KFactorResponse = z.infer<typeof KFactorResponse>;

/**
 * Вспомогательный тип: счётчики за три окна d1/d7/d30.
 * Используется в OverviewResponse для newUsers, activeAuthors, anonAskOpens.
 */
export const WindowCounts = z
  .object({
    d1: z.number().int().nonnegative(),
    d7: z.number().int().nonnegative(),
    d30: z.number().int().nonnegative(),
  })
  .strict();
export type WindowCounts = z.infer<typeof WindowCounts>;

/**
 * Здоровье контента за всё время (work-028 B) — ТОЛЬКО counts из `Question`/`Answer`.
 *
 * `questionStatus` — 4 ключа ровно как enum `status` в `schema.prisma:72`
 * (pending | answered | hidden | removed). `flagged`/`bypassed` — честные
 * счётчики без синтетического «blocked» (см. work-028 план, fix БЛОКЕР-1):
 *   - flagged = вопросы с moderationFlag !== null (flagged+blocked вместе).
 *   - bypassed = вопросы, прошедшие без ML-проверки (moderationBypassed=true).
 */
export const ContentHealth = z
  .object({
    totalQuestions: z.number().int().nonnegative(),
    totalAnswers: z.number().int().nonnegative(),
    questionStatus: z
      .object({
        pending: z.number().int().nonnegative(),
        answered: z.number().int().nonnegative(),
        hidden: z.number().int().nonnegative(),
        removed: z.number().int().nonnegative(),
      })
      .strict(),
    flagged: z.number().int().nonnegative(),
    bypassed: z.number().int().nonnegative(),
  })
  .strict();
export type ContentHealth = z.infer<typeof ContentHealth>;

/**
 * Разрез по языку — счётчик юзеров с данным `languageCode` (или `'unknown'`
 * для null). k-анонимный порог (`LANG_FLOOR` в сервисе) отфильтровывает узкие
 * bucket'ы до попадания сюда — см. `computeOverview`/threat-model контроль #15.
 */
export const LanguageCount = z
  .object({
    code: z.string(),
    count: z.number().int().nonnegative(),
  })
  .strict();
export type LanguageCount = z.infer<typeof LanguageCount>;

/**
 * Аудитория (work-028 C) — безопасные разрезы, ТОЛЬКО counts из `User`.
 *
 * `languages` — топ (до 12) языков над k-анонимным порогом `LANG_FLOOR`,
 * отсортированных по count desc; узкие языки свёрнуты в `otherLanguages`
 * (защита от «единственный носитель редкого языка» как identity-сигнала).
 */
export const AudienceBreakdown = z
  .object({
    premium: z.number().int().nonnegative(),
    notificationsOn: z.number().int().nonnegative(),
    languages: z.array(LanguageCount),
    otherLanguages: z.number().int().nonnegative(),
  })
  .strict();
export type AudienceBreakdown = z.infer<typeof AudienceBreakdown>;

/**
 * Response `GET /analytics/overview` — сводка посещаемости и активности.
 *
 * Инварианты приватности (threat-model analytics.md I.c/контроль #11):
 *   - ТОЛЬКО числа. Никаких userId/boxId/списков субъектов.
 *   - activeAuthors = авторы с ЛЮБЫМ авторским событием (включая app_opened).
 *   - anonAskOpens = объём анонимных заходов (ask_opened, userId=null).
 *   - activity30d = counts по всем 9 типам LoopEvent за 30 дней (work-035: +ad_start).
 *   - Окна накопительные: d1 ⊂ d7 ⊂ d30.
 *   - content/audience (work-028) — те же инварианты: только counts/агрегаты.
 */
export const OverviewResponse = z
  .object({
    /** ISO-таймштамп генерации (из кэша — момент последней генерации). */
    generatedAt: z.string(),
    /** Всего зарегистрированных юзеров. */
    totalUsers: z.number().int().nonnegative(),
    /** Новые юзеры за d1/d7/d30 (накопительные окна). */
    newUsers: WindowCounts,
    /** Активные авторы за d1/d7/d30 (distinct userId с авторским событием). */
    activeAuthors: WindowCounts,
    /** Анонимные заходы на ASK-ссылку за d1/d7/d30 (объём, не уникальные). */
    anonAskOpens: WindowCounts,
    /** Воронка событий за последние 30 дней (все 9 типов, 0 для отсутствующих). */
    activity30d: FunnelCounts,
    /** Здоровье контента за всё время (work-028 B). */
    content: ContentHealth,
    /** Разрезы аудитории — Premium/уведомления/языки (work-028 C). */
    audience: AudienceBreakdown,
  })
  .strict();
export type OverviewResponse = z.infer<typeof OverviewResponse>;

/**
 * Response `GET /me/admin` — статус администратора текущего пользователя.
 *
 * Отдаёт ТОЛЬКО собственный boolean вызывающему (не enumeration-оракул).
 * Приемлемо: подтверждает наличие admin-контура, но не раскрывает список
 * других администраторов (threat-model analytics.md трейд-офф (a)).
 */
export const AdminStatusResponse = z.object({ isAdmin: z.boolean() }).strict();
export type AdminStatusResponse = z.infer<typeof AdminStatusResponse>;
